import Text from '~/common/components/Texts/Text';
import { formatCurrency } from '~/common/util/format';

import BreakdownContainer from '../BreakdownContainer';
import lang from '../language.json';
import ConsideredFormulas from '../styles/ConsideredFormulas';
import HeadingAndText from './HeadingAndText';
import PaystubYTD from './PaystubYTD';
import W2ItemsYTD from './W2ItemsYTD';

type FormulaDisplayProps = {
  formula?: string;
  consideredFormulas?: {
    formula?: string;
    formulaType?: string;
    incomeValue?: number;
    isFormulaUsed?: boolean;
  }[];
};

type W2Item = {
  fiscalYear?: number;
  monthlyAmount?: number;
  annualIncome?: number;
};

type PastYearItem = {
  fiscalYear?: number;
  annualIncome?: number;
  monthlyAmount?: number;
  totalBackedInVariableIncomeFormula?: string | null;
  backingIntoVariableIncomeFormula?: string | null;
};

type CurrentYearIncomeItem = {
  incomeType?: string;
  monthlyAmount?: number;
  annualIncome?: number;
  totalYearToDateIncome?: number;
};

type CurrentYearItems = {
  fiscalYear?: number;
  currentYearIncome?: CurrentYearIncomeItem[];
};

type VariableIncomeBreakdownProps = {
  /** The accordion title*/
  title?: string;
  /** The subtitle on the accordion*/
  trendTitle?: string;
  /** The equation for the current calculation*/
  formula?: string | null;
  /** The variable calculation title*/
  calculatedVariableTitle?: string;
  /** The calculated amount*/
  calculatedVariableAmount?: number;
  /** The w2 objects  */
  w2Items?: W2Item[];
  /** The past years data*/
  pastYearsItems?: PastYearItem[];
  /** The current year income data*/
  currentYearItems?: CurrentYearItems;
  /** The formula and values used for calculations */
  variablePercentageFormula?: string;
  /** The base income is variable or not */
  isBaseIncomeVariable?: boolean;
  /** List of possible formulas for WVOE */
  consideredFormulas?: {
    formula?: string;
    formulaType?: string;
    incomeValue?: number;
    isFormulaUsed?: boolean;
  }[];
  hideAccordion?: boolean;
};

function FormulaDisplay({
  formula,
  consideredFormulas,
}: Readonly<FormulaDisplayProps>) {
  if (consideredFormulas?.length) {
    return (
      <div data-testid="variable-formula">
        <Text fontWeight="bold" mb={1}>
          Calculations:
        </Text>
        <ConsideredFormulas consideredFormulas={consideredFormulas} />
      </div>
    );
  }

  if (!formula) {
    return null;
  }

  return (
    <div data-testid="variable-formula">
      <HeadingAndText heading="Calculation:" text={formula} />
    </div>
  );
}

// HeadingText and calculation data should be refactored to use a data-list (<dl></dl>)
const VariableIncomeBreakdown = ({
  title = '',
  w2Items = [],
  formula = null,
  trendTitle = '',
  currentYearItems = {},
  pastYearsItems = [],
  calculatedVariableTitle = '',
  calculatedVariableAmount = 0,
  isBaseIncomeVariable = false,
  variablePercentageFormula,
  consideredFormulas,
  hideAccordion,
}: Readonly<VariableIncomeBreakdownProps>) => {
  const { fiscalYear = 0, currentYearIncome = [] } = currentYearItems;
  const isBaseIncome = title.includes('Base');
  let showStaticHeading = true;
  if (isBaseIncome) {
    showStaticHeading =
      !trendTitle.includes('Declining') || trendTitle.includes('Stabilizing');
  }

  // FHA doesn't use trends, so don't show on screen
  if (trendTitle === '- Classic') {
    trendTitle = '';
  }

  return (
    <BreakdownContainer
      header={title}
      subheader={trendTitle}
      calculatedAmount={calculatedVariableAmount}
      hideAccordion={hideAccordion}
    >
      {!isBaseIncomeVariable && (
        <div data-testid="non-variable-static-text">
          <HeadingAndText
            heading="Percentage:"
            text={lang.W2_NON_VARIABLE_INCOME.PERCENTAGE}
          />
          <HeadingAndText
            heading={`Last Year Income:`}
            text={lang.W2_NON_VARIABLE_INCOME.LAST_YEAR_INCOME}
          />
          <HeadingAndText
            heading={`Prior Year Income:`}
            text={lang.W2_NON_VARIABLE_INCOME.PRIOR_YEAR_INCOME}
          />
          <HeadingAndText
            heading="Calculation Equations:"
            text={lang.W2_NON_VARIABLE_INCOME.CALCULATION_EQUATIONS}
          />
        </div>
      )}
      {isBaseIncomeVariable && showStaticHeading && (
        <div data-testid="variable-static-text">
          <HeadingAndText
            heading="Percentage:"
            text={lang.W2_VARIABLE_INCOME.PERCENTAGE}
          />
          <HeadingAndText
            heading="Calculation Equation:"
            text={lang.W2_VARIABLE_INCOME.CALCULATION_EQUATION}
          />
        </div>
      )}
      {!!currentYearIncome.length && (
        <div data-testid="paystub-ytd">
          <PaystubYTD
            fiscalYear={fiscalYear}
            currentYearIncome={currentYearIncome}
            variableTitle={
              isBaseIncomeVariable
                ? calculatedVariableTitle
                : `Variable ${calculatedVariableTitle
                    .replaceAll(':', '')
                    .replace('Income', '')}`
            }
          />
        </div>
      )}
      {showStaticHeading && (
        <Text mb={10}>Percentage: {variablePercentageFormula}</Text>
      )}
      {!!pastYearsItems.length && !!w2Items.length && (
        <div data-testid="w2-items-ytd">
          <W2ItemsYTD
            pastYearItems={pastYearsItems}
            w2Items={w2Items}
            variableTitle={
              isBaseIncomeVariable
                ? calculatedVariableTitle
                : `Variable ${calculatedVariableTitle
                    .replaceAll(':', '')
                    .replace('Income', '')}`
            }
            showCalcEquation={showStaticHeading}
          />
        </div>
      )}
      <FormulaDisplay
        formula={formula}
        consideredFormulas={consideredFormulas}
      />
      <HeadingAndText
        heading={calculatedVariableTitle}
        text={formatCurrency(calculatedVariableAmount)}
      />
    </BreakdownContainer>
  );
};

export default VariableIncomeBreakdown;
